/**
 * A forgotten password, before signing in (ADR-0053): ask for a link by work
 * address, then choose a new password on the page the link opens. The
 * answer to a request is always the same, so this page never tells anyone
 * who has a recovery email.
 */

import { useEffect, useState } from 'react';

import { api, ApiError } from '../admin/api';
import { MailIcon, ShieldIcon } from '../components/icons';

/** The recovery page this path names, or null. */
export function recoveryRoute(
  pathname: string,
): { readonly page: 'forgot' } | { readonly page: 'reset'; readonly token: string } | null {
  if (pathname === '/forgot-password') {
    return { page: 'forgot' };
  }
  const m = /^\/recover\/([A-Za-z0-9_-]{20,})\/?$/.exec(pathname);
  return m?.[1] ? { page: 'reset', token: m[1] } : null;
}

function Frame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="auth auth-plain">
      <header className="auth-top">
        <span className="auth-logo">
          <img src="/brand/genworx-logo.png" alt="Genworx" width="131" height="24" />
        </span>
        <span className="auth-brand-name">Cloud</span>
      </header>
      <main className="auth-card">{children}</main>
      <footer className="auth-foot">
        <span>&copy; {new Date().getFullYear()} Genworx Pte Ltd</span>
      </footer>
    </div>
  );
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong. Try again.';
}

export function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  return (
    <Frame>
      <h2>Reset your password</h2>
      {sent ? (
        <>
          <div className="alert ok" role="status">
            {sent}
          </div>
          <p className="auth-lede">
            The link in that email works once, for 30 minutes. Nothing arrived? Check the junk
            folder of your recovery email, or ask your organisation&apos;s administrator.
          </p>
          <a className="btn" href="/">
            Back to sign-in
          </a>
        </>
      ) : (
        <>
          <p className="auth-lede">
            Enter your work email address. If you set a recovery email, we send it a link to choose
            a new password.
          </p>
          {problem && (
            <div className="alert" role="alert">
              {problem}
            </div>
          )}
          <form
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              setBusy(true);
              setProblem(null);
              api<{ message: string }>('/recovery/request', {
                method: 'POST',
                body: { email: email.trim() },
              })
                .then((answer) => setSent(answer.message))
                .catch((error: unknown) => setProblem(message(error)))
                .finally(() => setBusy(false));
            }}
          >
            <label className="auth-label" htmlFor="forgot-email">
              Work email address
            </label>
            <div className="auth-input">
              <MailIcon />
              <input
                id="forgot-email"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoFocus
              />
            </div>
            <button className="btn" type="submit" disabled={busy || !email.trim()}>
              {busy ? 'Sending…' : 'Send me a link'}
            </button>
          </form>
          <p className="auth-lede auth-small">
            If your organisation signs you in with its own system (Microsoft, Google or your company
            directory), reset your password there.
          </p>
          <p className="auth-lede">
            <a href="/">Back to sign-in</a>
          </p>
        </>
      )}
    </Frame>
  );
}

type Link = { status: 'checking' } | { status: 'ok'; email: string } | { status: 'gone' };

export function ResetPassword({ token }: { token: string }): JSX.Element {
  const [link, setLink] = useState<Link>({ status: 'checking' });
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api<{ email: string }>('/recovery/check', {
      method: 'POST',
      body: { token },
      signal: controller.signal,
    })
      .then((found) => setLink({ status: 'ok', email: found.email }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLink({ status: 'gone' });
          if (!(error instanceof ApiError && error.status === 410)) {
            setProblem(message(error));
          }
        }
      });
    return () => controller.abort();
  }, [token]);

  if (done) {
    return (
      <Frame>
        <h2>Your password is changed</h2>
        <div className="alert ok" role="status">
          Every device was signed out. Sign in again with your new password; your authenticator code
          is asked as before.
        </div>
        <a className="btn" href="/">
          Sign in
        </a>
      </Frame>
    );
  }
  if (link.status === 'checking') {
    return (
      <Frame>
        <p className="auth-lede" role="status">
          Checking your link…
        </p>
      </Frame>
    );
  }
  if (link.status === 'gone') {
    return (
      <Frame>
        <h2>This link no longer works</h2>
        <p className="auth-lede">
          {problem ?? 'It has been used, or its 30 minutes have run out.'}
        </p>
        <a className="btn" href="/forgot-password">
          Ask for a new link
        </a>
      </Frame>
    );
  }
  const mismatch = again !== '' && again !== password;
  return (
    <Frame>
      <h2>Choose a new password</h2>
      <p className="auth-lede">
        For <strong>{link.email}</strong>. Your authenticator stays as it is.
      </p>
      {problem && (
        <div className="alert" role="alert">
          {problem}
        </div>
      )}
      <form
        className="auth-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (password !== again) {
            setProblem('The two passwords are not the same.');
            return;
          }
          setBusy(true);
          setProblem(null);
          api<void>('/recovery/reset', { method: 'POST', body: { token, password } })
            .then(() => setDone(true))
            .catch((error: unknown) => {
              if (error instanceof ApiError && error.status === 410) {
                setLink({ status: 'gone' });
              } else {
                setProblem(message(error));
              }
            })
            .finally(() => setBusy(false));
        }}
      >
        <label className="auth-label" htmlFor="new-password">
          New password
        </label>
        <div className="auth-input">
          <ShieldIcon />
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoFocus
          />
        </div>
        <label className="auth-label" htmlFor="new-password-again">
          The same again
        </label>
        <div className="auth-input">
          <ShieldIcon />
          <input
            id="new-password-again"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(event) => setAgain(event.target.value)}
            aria-invalid={mismatch}
            required
          />
        </div>
        {mismatch && <p className="auth-route">The two passwords are not the same yet.</p>}
        <button className="btn" type="submit" disabled={busy || !password || mismatch}>
          {busy ? 'Saving…' : 'Save new password'}
        </button>
      </form>
    </Frame>
  );
}
